import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ShieldAlert, Phone, Clock, Ban, FileSearch, Plus, Trash2,
  Upload, Search, CheckCircle, XCircle, AlertTriangle, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getComplianceDashboard, getDncList, addToDnc, bulkAddDnc,
  removeFromDnc, checkDnc, getCallingHours, updateCallingHours,
  getOptOuts, markOptOut, removeOptOut, getAuditLogs, getAuditActions, getAuditStats,
} from '../lib/api';

// ==================== DNC PANEL ====================
const DncPanel = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('');
  const [bulkPhones, setBulkPhones] = useState('');
  const [checkPhone, setCheckPhone] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDnc = useCallback(async () => {
    try {
      const res = await getDncList({ search, limit: 50 });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch { /* ignore */ }
  }, [search]);

  useEffect(() => { fetchDnc(); }, [fetchDnc]);

  const handleAdd = async () => {
    if (!newPhone.trim()) return;
    setLoading(true);
    try {
      await addToDnc({ phone: newPhone, reason: newReason || 'manual' });
      toast.success('Number added to DNC list');
      setShowAdd(false);
      setNewPhone('');
      setNewReason('');
      fetchDnc();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setLoading(false); }
  };

  const handleBulkAdd = async () => {
    const phones = bulkPhones.split('\n').map(p => p.trim()).filter(Boolean);
    if (!phones.length) return;
    setLoading(true);
    try {
      const res = await bulkAddDnc({ phones, reason: 'bulk_import' });
      toast.success(`Added ${res.data.added}, skipped ${res.data.skipped}`);
      setShowBulk(false);
      setBulkPhones('');
      fetchDnc();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk add failed');
    } finally { setLoading(false); }
  };

  const handleRemove = async (id) => {
    try {
      await removeFromDnc(id);
      toast.success('Removed from DNC list');
      fetchDnc();
    } catch { toast.error('Failed to remove'); }
  };

  const handleCheck = async () => {
    if (!checkPhone.trim()) return;
    try {
      const res = await checkDnc(checkPhone);
      setCheckResult(res.data);
    } catch { toast.error('Check failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Do Not Call List</h3>
          <p className="text-sm text-slate-500">{total} numbers on DNC list</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowBulk(true)} data-testid="bulk-dnc-btn">
            <Upload className="w-4 h-4 mr-1" />Bulk Import
          </Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setShowAdd(true)} data-testid="add-dnc-btn">
            <Plus className="w-4 h-4 mr-1" />Add Number
          </Button>
        </div>
      </div>

      {/* DNC Check */}
      <div className="bg-slate-50 rounded-lg p-4 border">
        <Label className="text-sm font-medium mb-2 block">Quick DNC Check</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Enter phone number to check..."
            value={checkPhone}
            onChange={e => setCheckPhone(e.target.value)}
            data-testid="dnc-check-input"
          />
          <Button variant="outline" onClick={handleCheck} data-testid="dnc-check-btn">
            <Search className="w-4 h-4 mr-1" />Check
          </Button>
        </div>
        {checkResult && (
          <div className={`mt-2 p-2 rounded text-sm flex items-center gap-2 ${checkResult.on_dnc ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {checkResult.on_dnc ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {checkResult.on_dnc ? `${checkResult.phone} is ON the DNC list` : `${checkResult.phone} is NOT on DNC list`}
          </div>
        )}
      </div>

      {/* Search & List */}
      <div className="flex gap-2">
        <Input
          placeholder="Search DNC list..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="dnc-search-input"
        />
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Phone</th>
              <th className="text-left p-3 font-medium text-slate-600">Reason</th>
              <th className="text-left p-3 font-medium text-slate-600">Added By</th>
              <th className="text-left p-3 font-medium text-slate-600">Date</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.id} className="border-b hover:bg-slate-50">
                <td className="p-3 font-mono">{entry.phone}</td>
                <td className="p-3 text-slate-600">{entry.reason}</td>
                <td className="p-3 text-slate-600">{entry.added_by_name}</td>
                <td className="p-3 text-slate-500">{new Date(entry.added_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => handleRemove(entry.id)} data-testid={`remove-dnc-${entry.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">No DNC entries found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Single Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="add-dnc-dialog">
          <DialogHeader><DialogTitle>Add to DNC List</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Phone Number</Label>
              <Input placeholder="+1 (555) 123-4567" value={newPhone} onChange={e => setNewPhone(e.target.value)} data-testid="dnc-phone-input" />
            </div>
            <div>
              <Label>Reason</Label>
              <Input placeholder="Customer request, legal, etc." value={newReason} onChange={e => setNewReason(e.target.value)} data-testid="dnc-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleAdd} disabled={loading} data-testid="confirm-add-dnc-btn">
              Add to DNC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="max-w-md" data-testid="bulk-dnc-dialog">
          <DialogHeader><DialogTitle>Bulk Import DNC Numbers</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Phone Numbers (one per line)</Label>
              <textarea
                className="w-full h-40 p-3 border rounded-lg text-sm font-mono resize-none"
                placeholder="+15551234567&#10;+15559876543&#10;(555) 111-2222"
                value={bulkPhones}
                onChange={e => setBulkPhones(e.target.value)}
                data-testid="bulk-dnc-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleBulkAdd} disabled={loading} data-testid="confirm-bulk-dnc-btn">
              Import All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CALLING HOURS PANEL ====================
const CallingHoursPanel = () => {
  const [config, setConfig] = useState({ start_hour: 8, end_hour: 21, blocked_days: [], enabled: true });
  const [saving, setSaving] = useState(false);

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    (async () => {
      try {
        const res = await getCallingHours();
        setConfig(res.data);
      } catch { /* use defaults */ }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCallingHours(config);
      toast.success('Calling hours updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const toggleDay = (dayIndex) => {
    const days = [...config.blocked_days];
    const idx = days.indexOf(dayIndex);
    if (idx >= 0) days.splice(idx, 1);
    else days.push(dayIndex);
    setConfig({ ...config, blocked_days: days });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-slate-900">Calling Hours Enforcement</h3>
        <p className="text-sm text-slate-500">TCPA-compliant calling hours based on lead timezone</p>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 border space-y-4">
        <div className="flex items-center gap-4">
          <Label className="font-medium">Enforcement</Label>
          <button
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
            onClick={() => setConfig({ ...config, enabled: !config.enabled })}
            data-testid="calling-hours-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm ${config.enabled ? 'text-green-600' : 'text-slate-500'}`}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Start Hour (local time)</Label>
            <select
              className="w-full border rounded-lg p-2 text-sm mt-1"
              value={config.start_hour}
              onChange={e => setConfig({ ...config, start_hour: parseInt(e.target.value) })}
              data-testid="start-hour-select"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm">End Hour (local time)</Label>
            <select
              className="w-full border rounded-lg p-2 text-sm mt-1"
              value={config.end_hour}
              onChange={e => setConfig({ ...config, end_hour: parseInt(e.target.value) })}
              data-testid="end-hour-select"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-sm mb-2 block">Blocked Days (no calling)</Label>
          <div className="flex flex-wrap gap-2">
            {dayNames.map((day, i) => (
              <button
                key={i}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${config.blocked_days.includes(i) ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}
                onClick={() => toggleDay(i)}
                data-testid={`block-day-${i}`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">TCPA Compliance Note</p>
            <p className="mt-1">Federal law (TCPA) restricts calls to consumers between 8:00 AM and 9:00 PM in the consumer's local time zone. Violations can result in $500-$1,500 per call.</p>
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-calling-hours-btn">
        Save Configuration
      </Button>
    </div>
  );
};

// ==================== OPT-OUT PANEL ====================
const OptOutPanel = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchOptOuts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOptOuts({ limit: 50 });
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOptOuts(); }, [fetchOptOuts]);

  const handleRemoveOptOut = async (leadId) => {
    try {
      await removeOptOut(leadId);
      toast.success('Opt-out removed');
      fetchOptOuts();
    } catch { toast.error('Failed to remove opt-out'); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-slate-900">SMS Opt-Out Tracking</h3>
        <p className="text-sm text-slate-500">{total} leads have opted out of SMS</p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Customer</th>
              <th className="text-left p-3 font-medium text-slate-600">Phone</th>
              <th className="text-left p-3 font-medium text-slate-600">Status</th>
              <th className="text-left p-3 font-medium text-slate-600">DNC</th>
              <th className="text-left p-3 font-medium text-slate-600">Last Updated</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <tr key={lead.id} className="border-b hover:bg-slate-50">
                <td className="p-3 font-medium">{lead.customer_name}</td>
                <td className="p-3 font-mono text-slate-600">{lead.phone}</td>
                <td className="p-3">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">{lead.status}</span>
                </td>
                <td className="p-3">
                  {lead.do_not_call ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">DNC</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">No</span>
                  )}
                </td>
                <td className="p-3 text-slate-500">{lead.updated_at ? new Date(lead.updated_at).toLocaleDateString() : '-'}</td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveOptOut(lead.id)} data-testid={`remove-optout-${lead.id}`}>
                    <XCircle className="w-4 h-4 text-slate-400" />
                  </Button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">No opted-out leads</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== AUDIT LOG PANEL ====================
const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [filters, setFilters] = useState({ action_type: '', limit: 50 });
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: filters.limit };
      if (filters.action_type) params.action_type = filters.action_type;
      const res = await getAuditLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, actionsRes] = await Promise.all([getAuditStats(), getAuditActions()]);
      setStats(statsRes.data);
      setActionTypes(actionsRes.data.action_types || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatAction = (action) => {
    const map = {
      'lead_status_changed': 'Status Changed',
      'dnc_added': 'DNC Added',
      'dnc_removed': 'DNC Removed',
      'dnc_flagged': 'DNC Flagged',
      'dnc_bulk_added': 'Bulk DNC Import',
      'sms_opt_out': 'SMS Opt-Out',
      'sms_opt_out_removed': 'Opt-Out Removed',
      'calling_hours_updated': 'Calling Hours Updated',
    };
    return map[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getActionColor = (action) => {
    if (action.includes('dnc') || action.includes('opt_out')) return 'bg-red-50 text-red-700';
    if (action.includes('status')) return 'bg-blue-50 text-blue-700';
    if (action.includes('calling')) return 'bg-amber-50 text-amber-700';
    return 'bg-slate-50 text-slate-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Audit Logs</h3>
          <p className="text-sm text-slate-500">{total} total log entries{stats ? `, ${stats.today_count} today` : ''}</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 border">
            <p className="text-2xl font-bold text-slate-900">{stats.total_logs}</p>
            <p className="text-xs text-slate-500">Total Events</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <p className="text-2xl font-bold text-blue-700">{stats.today_count}</p>
            <p className="text-xs text-blue-600">Today</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
            <p className="text-2xl font-bold text-amber-700">{stats.by_action_type?.length || 0}</p>
            <p className="text-xs text-amber-600">Action Types</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        <select
          className="border rounded-lg p-2 text-sm"
          value={filters.action_type}
          onChange={e => setFilters({ ...filters, action_type: e.target.value })}
          data-testid="audit-filter-select"
        >
          <option value="">All Actions</option>
          {actionTypes.map(a => (
            <option key={a} value={a}>{formatAction(a)}</option>
          ))}
        </select>
      </div>

      {/* Log Table */}
      <div className="border rounded-lg overflow-hidden">
        <ScrollArea className="h-96">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b sticky top-0">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Time</th>
                <th className="text-left p-3 font-medium text-slate-600">Action</th>
                <th className="text-left p-3 font-medium text-slate-600">User</th>
                <th className="text-left p-3 font-medium text-slate-600">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 text-slate-500 whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action_type)}`}>
                      {formatAction(log.action_type)}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{log.user_name}</td>
                  <td className="p-3 text-slate-500 text-xs max-w-xs truncate">
                    {log.details?.customer_name && <span className="font-medium">{log.details.customer_name} </span>}
                    {log.details?.phone && <span className="font-mono">{log.details.phone} </span>}
                    {log.details?.old_status && <span>{log.details.old_status} → {log.details.new_status}</span>}
                    {log.details?.count && <span>{log.details.count} numbers</span>}
                    {log.details?.reason && !log.details?.customer_name && <span>{log.details.reason}</span>}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-400">No audit logs yet</td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
};

// ==================== MAIN COMPLIANCE PANEL ====================
const CompliancePanel = () => {
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('dnc');

  useEffect(() => {
    (async () => {
      try {
        const res = await getComplianceDashboard();
        setDashboard(res.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const tabs = [
    { key: 'dnc', label: 'DNC List', icon: Ban },
    { key: 'calling-hours', label: 'Calling Hours', icon: Clock },
    { key: 'opt-outs', label: 'SMS Opt-Outs', icon: Phone },
    { key: 'audit-logs', label: 'Audit Logs', icon: FileSearch },
  ];

  return (
    <div className="space-y-6" data-testid="compliance-panel">
      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-red-50 rounded-lg p-4 border border-red-100">
            <div className="flex items-center gap-2 mb-1">
              <Ban className="w-4 h-4 text-red-600" />
              <span className="text-xs text-red-600 font-medium">DNC Numbers</span>
            </div>
            <p className="text-2xl font-bold text-red-700" data-testid="dnc-count">{dashboard.dnc_count}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4 text-orange-600" />
              <span className="text-xs text-orange-600 font-medium">SMS Opt-Outs</span>
            </div>
            <p className="text-2xl font-bold text-orange-700" data-testid="optout-count">{dashboard.opted_out_count}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-600 font-medium">Flagged Leads</span>
            </div>
            <p className="text-2xl font-bold text-amber-700" data-testid="flagged-count">{dashboard.flagged_leads}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-blue-600 font-medium">Today's Audits</span>
            </div>
            <p className="text-2xl font-bold text-blue-700" data-testid="audit-count">{dashboard.today_audits}</p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`compliance-tab-${tab.key}`}
          >
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border p-6">
        {activeTab === 'dnc' && <DncPanel />}
        {activeTab === 'calling-hours' && <CallingHoursPanel />}
        {activeTab === 'opt-outs' && <OptOutPanel />}
        {activeTab === 'audit-logs' && <AuditLogPanel />}
      </div>
    </div>
  );
};

export default CompliancePanel;
